"use client";

// ─────────────────────────────────────────────────────────────────────────────
// Price A/B experiment framework (color analysis) — client-side assignment.
//
// One place to define the live experiment. A visitor is assigned to a bucket
// ONCE and it sticks (localStorage), so nobody ever sees one price today and a
// different price tomorrow — that inconsistency is a trust/refund risk, which
// is exactly what these tests are trying to avoid.
//
// Every bucket carries an explicit `priceCents` — the amount that will actually
// be charged. Display components pass `bucket.id` to the API routes; the
// routes resolve the actual charge amount via `resolveBucket` in
// priceExperiment.config.ts (they never trust a client-sent amount).
//
// Judge winners by REVENUE PER VISITOR (conversion × price), not conversion
// alone. Every funnel event is tagged with the bucket + priceCents so Gizmo can
// compute RPV per arm (breakdown_by_property on `bucket`).
// ─────────────────────────────────────────────────────────────────────────────

import { PRICE_EXPERIMENT } from "./priceExperiment.config";
import type { PriceBucket } from "./priceExperiment.config";

export { PRICE_EXPERIMENT, bucketEventMeta, resolveBucket } from "./priceExperiment.config";
export type { PriceBucket, PriceExperiment } from "./priceExperiment.config";

const STORAGE_KEY = "ca_price_bucket";

// Server-safe fallback + the default any non-browser render should use. Must
// match resolveBucket(undefined)'s fallback (the control bucket), so SSR and
// server-resolved charges always agree on which bucket "no assignment" means.
const controlBucket = (): PriceBucket => PRICE_EXPERIMENT.buckets[PRICE_EXPERIMENT.buckets.length - 1];

// Deterministic-per-call weighted pick. Only called in the browser at assignment
// time; Math.random is fine here (this is client bucketing, not the workflow VM).
const rollBucket = (): PriceBucket => {
  const total = PRICE_EXPERIMENT.buckets.reduce((s, b) => s + b.weight, 0);
  let r = Math.random() * total;
  for (const b of PRICE_EXPERIMENT.buckets) {
    r -= b.weight;
    if (r <= 0) return b;
  }
  return controlBucket();
};

/**
 * Returns this visitor's sticky bucket. Assigns + persists on first call.
 * SSR-safe: returns control on the server (real assignment happens in the
 * browser via useEffect in the display components).
 */
export const getPriceBucket = (): PriceBucket => {
  if (typeof window === "undefined") return controlBucket();

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as { key: string; id: string };
      // Same experiment key + still a valid bucket id → reuse the sticky choice.
      if (saved.key === PRICE_EXPERIMENT.key) {
        const match = PRICE_EXPERIMENT.buckets.find((b) => b.id === saved.id);
        if (match) return match;
      }
    }
  } catch {
    // Corrupt/blocked storage — fall through to a fresh roll.
  }

  const bucket = rollBucket();
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ key: PRICE_EXPERIMENT.key, id: bucket.id }));
  } catch {
    // Storage blocked (private mode). Still return a bucket; it just won't stick.
  }
  return bucket;
};
